import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { accountQuery, resetPassword } from '@/api';
import { ResetPasswordModal, type ResetPasswordFormValues } from '@/ui/auth';

export const Route = createFileRoute('/resetpassword/$guid/$channel')({
  component: ResetPasswordChannelPage,
});

function ResetPasswordChannelPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { guid, channel } = Route.useParams();
  const { data: account } = useQuery(accountQuery());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const chatEnabled = account?.Chat === true;
  const complexPassword = account?.ComplexPassword === true;
  const isSms = channel.toLowerCase() === 'sms';

  const leave = () => {
    void navigate({ href: success ? '/login' : '/forgotpassword' });
  };

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    setLoading(true);
    setError(null);
    try {
      const result = await resetPassword({
        Guid: guid,
        Password: values.password,
        ConfirmPassword: values.confirmPassword,
        ...(isSms && values.code ? { Code: values.code } : {}),
      });
      if (result.Success === false) {
        const key = result.Error || 'Error';
        setError(t(key, { defaultValue: key }));
        return;
      }
      setSuccess(true);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Error';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetPasswordModal
      open
      dismissible
      onClose={leave}
      loading={loading}
      error={error}
      chatEnabled={chatEnabled}
      requireCode={isSms}
      complexPassword={complexPassword}
      showSmsTimer={isSms}
      success={success}
      onSubmit={handleSubmit}
    />
  );
}
